/**
 * Scroll-dock: bottom living bar (section skins + prompt) yields to the
 * in-page hero/line at the top of the page, then takes over once the visitor
 * scrolls past that launcher.
 *
 *   - Hero/line substantially visible → sticky OFF (hero owns discovery)
 *   - Hero/line mostly scrolled away → sticky ON  (section dual-chrome)
 *   - Scroll back to top             → sticky OFF again
 *   - Nav-only (no hero/line content) → sticky always ON (only launcher)
 */

export type ScrollDockOptions = {
  customerId: string;
  /** Called with true when sticky should be visible. */
  onStickyChange: (stickyVisible: boolean) => void;
  /**
   * Fraction of the in-page launcher still visible before we hide sticky.
   * Below this ratio the living bar shows. Default 0.35.
   */
  hideWhenVisibleRatio?: number;
  /**
   * FAIL CLOSED until the tenant config lands (#589). Before this flips the
   * bar never shows: the pre-config frames used to paint the UNSKINNED
   * default bar (no palette, fallback copy, and — worse — un-scoped: a rule
   * denying the bar on this URL had not arrived yet). Seed true when the
   * config already landed before the dock was built (config-before-mount).
   */
  configReady?: boolean;
  /**
   * Tenant reveal option (#589): hold the bar until the visitor's first
   * scroll gesture, then hand over to the normal hero-yield logic. The
   * latch persists across SPA route changes — once revealed, stays
   * revealed for the page's lifetime.
   */
  revealOnScroll?: boolean;
};

export type ScrollDockHandle = {
  disconnect: () => void;
  /** Force re-evaluate (e.g. after SPA nav re-renders placements). */
  refresh: () => void;
  /** Config landed — the dock may now show the bar (subject to the other
   *  gates). Idempotent. */
  markConfigReady: () => void;
  /** Live tenant setting (a dashboard save mid-session re-posts config). */
  setRevealOnScroll: (v: boolean) => void;
};

function primaryPlacementSelector(customerId: string): string {
  // Prefer hero_bar, else in_content_line (anchors bhai-<id>-{hero,line}).
  const id = CSS.escape(customerId);
  return `div.bhai-${id}-hero, div.bhai-${id}-line`;
}

function findPrimaryLauncher(customerId: string): Element | null {
  const nodes = document.querySelectorAll(primaryPlacementSelector(customerId));
  for (const n of nodes) {
    if (n.childElementCount > 0) return n;
  }
  return null;
}

/** How much of the element is currently inside the viewport [0, 1]. */
function visibleRatio(el: Element): number {
  const rect = el.getBoundingClientRect();
  if (rect.height <= 0 || rect.width <= 0) return 0;
  const visibleH =
    Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
  if (visibleH <= 0) return 0;
  return Math.max(0, Math.min(1, visibleH / rect.height));
}

/** The dock's whole show/hide decision as a pure function — the #589 gates
 *  AND the original hero-yield rule in one place, unit-testable without a
 *  DOM. `launcherVisibleRatio` null = nav-only page (no in-page launcher).
 *
 *  Gates can only keep the bar OFF, never force it on:
 *    - !configReady   → never show (the pre-config frames used to paint the
 *                       unskinned, un-scoped default bar — the #589 flash).
 *    - revealOnScroll → hold until the visitor's first scroll gesture.
 */
export function dockShouldShow(s: {
  configReady: boolean;
  revealOnScroll: boolean;
  hasScrolled: boolean;
  launcherVisibleRatio: number | null;
  hideWhenVisibleRatio: number;
}): boolean {
  if (!s.configReady) return false;
  if (s.revealOnScroll && !s.hasScrolled) return false;
  if (s.launcherVisibleRatio === null) return true; // nav-only → bar is the only launcher
  return s.launcherVisibleRatio < s.hideWhenVisibleRatio;
}

export function setupScrollDock(opts: ScrollDockOptions): ScrollDockHandle {
  const hideWhenVisibleRatio = opts.hideWhenVisibleRatio ?? 0.35;
  let lastSticky: boolean | null = null;
  let io: IntersectionObserver | null = null;
  let listening = false;
  // #589 gates. Both AND into every show decision; hide decisions pass
  // through unchanged (a gate can only keep the bar OFF, never force it on).
  let configReady = opts.configReady === true;
  let revealOnScroll = opts.revealOnScroll === true;
  let hasScrolled = false;

  const setSticky = (v: boolean) => {
    if (lastSticky === v) return;
    lastSticky = v;
    opts.onStickyChange(v);
  };


  const onFirstScroll = () => {
    if (hasScrolled) return;
    hasScrolled = true;
    window.removeEventListener("scroll", onFirstScroll, true);
    window.removeEventListener("wheel", onFirstScroll, true);
    window.removeEventListener("touchmove", onFirstScroll, true);
    evaluate();
  };
  // Always latch (cheap, passive) — the setting can arrive AFTER the first
  // scroll on a slow preload, and the latch must already be true then.
  window.addEventListener("scroll", onFirstScroll, { passive: true, capture: true });
  window.addEventListener("wheel", onFirstScroll, { passive: true, capture: true });
  window.addEventListener("touchmove", onFirstScroll, { passive: true, capture: true });

  const evaluate = () => {
    const target = findPrimaryLauncher(opts.customerId);
    setSticky(
      dockShouldShow({
        configReady,
        revealOnScroll,
        hasScrolled,
        launcherVisibleRatio: target ? visibleRatio(target) : null,
        hideWhenVisibleRatio,
      }),
    );
  };

  const stopScrollListen = () => {
    if (!listening) return;
    listening = false;
    window.removeEventListener("scroll", evaluate, true);
    window.removeEventListener("resize", evaluate);
  };

  const startScrollListen = () => {
    if (listening) return;
    listening = true;
    window.addEventListener("scroll", evaluate, { passive: true, capture: true });
    window.addEventListener("resize", evaluate, { passive: true });
  };

  const attach = () => {
    io?.disconnect();
    io = null;
    stopScrollListen();

    const target = findPrimaryLauncher(opts.customerId);
    if (!target) {
      // No launcher to observe — but the show decision still goes through
      // evaluate() so the #589 gates apply to the nav-only branch too (this
      // exact short-circuit was the pre-config default-bar paint).
      evaluate();
      return;
    }

    io = new IntersectionObserver(
      () => {
        evaluate();
      },
      {
        threshold: [0, 0.1, 0.25, 0.35, 0.5, 0.75, 1],
        rootMargin: "0px",
      },
    );
    io.observe(target);
    startScrollListen();
    evaluate();
  };

  attach();

  return {
    disconnect() {
      io?.disconnect();
      io = null;
      stopScrollListen();
      window.removeEventListener("scroll", onFirstScroll, true);
      window.removeEventListener("wheel", onFirstScroll, true);
      window.removeEventListener("touchmove", onFirstScroll, true);
    },
    refresh() {
      // Placements re-rendered — re-bind observer to the new node.
      lastSticky = null;
      attach();
    },
    markConfigReady() {
      if (configReady) return;
      configReady = true;
      evaluate();
    },
    setRevealOnScroll(v: boolean) {
      if (revealOnScroll === v) return;
      revealOnScroll = v;
      evaluate();
    },
  };
}
